import type { CSSProperties } from "react";
import { cn } from "@newxsystems/shared";
import { resolveTootPrimitive } from "../../../../client";
import type { ITootMediaFrameProps } from "./TootMediaFrame.props";
import { TootPlayableEmbed } from "./TootPlayableEmbed.client";

const Image = resolveTootPrimitive("Image");

export const TootMediaFrame = ({
  alt = "",
  aspectRatio = "16 / 9",
  caption,
  className,
  credit,
  embedTitle,
  focalPoint,
  height,
  kind = "image",
  loading = "lazy",
  objectPosition,
  playOnPosterClick = false,
  poster,
  posterAlt,
  sizes,
  src,
  srcSet,
  width,
}: ITootMediaFrameProps) => {
  if (!src) {
    return null;
  }

  const mediaStyle: CSSProperties = { aspectRatio };
  const resolvedObjectPosition = focalPoint
    ? `${focalPoint.x * 100}% ${focalPoint.y * 100}%`
    : objectPosition;
  const imageStyle: CSSProperties | undefined = resolvedObjectPosition
    ? { objectPosition: resolvedObjectPosition }
    : undefined;
  const hasCaption = Boolean(caption || credit);

  return (
    <figure
      className={cn(
        "toot-media-frame overflow-hidden rounded-[var(--toot-radius-card)] border border-toot-grid/90 bg-toot-surface-terminal text-[var(--toot-color-foreground)]",
        className,
      )}
    >
      <div style={mediaStyle}>
        {kind === "embed" && src ? (
          playOnPosterClick && poster ? (
            <TootPlayableEmbed
              alt={posterAlt ?? alt}
              embedTitle={embedTitle}
              height={height}
              loading={loading}
              objectPosition={resolvedObjectPosition}
              poster={poster}
              sizes={sizes}
              src={src}
              srcSet={srcSet}
              width={width}
            />
          ) : (
            <iframe
              className="relative h-full w-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading={loading}
              referrerPolicy="strict-origin-when-cross-origin"
              src={src}
              title={embedTitle}
            />
          )
        ) : kind === "video" && src ? (
          <video
            aria-label={embedTitle}
            className={cn("relative h-full w-full object-cover", poster && "cursor-pointer")}
            controls
            playsInline
            poster={poster}
            preload="metadata"
          >
            <source src={src} />
          </video>
        ) : (
          <Image
            alt={alt}
            className="absolute inset-0 h-full w-full object-cover"
            draggable={false}
            height={height}
            loading={loading}
            sizes={sizes}
            src={src}
            srcSet={srcSet}
            style={imageStyle}
            width={width}
          />
        )}
      </div>
      {hasCaption ? (
        <figcaption className="flex flex-col gap-1 border-t border-toot-grid/90 px-4 py-3 text-xs text-toot-muted text-start">
          {caption ? <span>{caption}</span> : <span />}
          {credit ? (
            <span className="uppercase tracking-[0.18em] text-toot-orange">{credit}</span>
          ) : null}
        </figcaption>
      ) : null}
    </figure>
  );
};

TootMediaFrame.displayName = "Surface::TootMediaFrame";
